import React, { createContext, useState, useRef, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Spacer,
  HStack,
  VStack,
} from "@chakra-ui/react";
import AvatarEditor from 'react-avatar-editor'
import { useMeasure } from "react-use";
import useTranslations from '../../hooks/useTranslations';

type AvatarEditorEditOptions = {
  title?: string,
  file?: File,
  onComplete?: (result: string) => void
}

export const AvatarEditorContext = createContext<
  (props: AvatarEditorEditOptions) => void | null
>(null);

const AvatarEditorProvider: React.FC = ({ children }) => {
  const t = useTranslations();

  const [ options, setOptions ] = useState<AvatarEditorEditOptions>({});
  const [ scale, setScale ] = useState(1.0);

  const editorRef = useRef(null);

  const [ modalBodyRef, { width } ] = useMeasure();

  const editAvatar = useCallback((options) => {
    setOptions(options);
  }, [setOptions]);

  const handleClose = useCallback(() => {
    setOptions({});
  }, [setOptions]);

  const handleSave = useCallback(() => {
    handleClose();
    options.onComplete(
      editorRef.current.getImageScaledToCanvas().toDataURL()
    );
  }, [options.onComplete, editorRef, handleClose])

  return (
    <AvatarEditorContext.Provider value={editAvatar}>
      {children}
      <Modal isOpen={!!options.file} onClose={handleClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{options.title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody ref={modalBodyRef}>
            <VStack>
              <AvatarEditor
                ref={editorRef}
                image={options.file}
                width={width}
                height={width}
                scale={scale}
                disableBoundaryChecks={true}
                border={0}
                borderRadius={width}
              />
              <Spacer />
              <Slider
                aria-label="scale"
                step={0.01}
                min={0.1}
                max={2.0}
                defaultValue={scale}
                onChange={setScale}
              >
                <SliderTrack>
                  <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb />
              </Slider>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button
                onClick={handleClose}
                data-test-id="avatarEditorCancelButton"
              >
                {t("forms.actions.cancel")}
              </Button>
              <Spacer />
              <Button
                colorScheme="blue"
                onClick={handleSave}
                data-test-id="avatarEditorSaveButton"
              >
                {t("forms.actions.save")}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AvatarEditorContext.Provider>
  );
}

export default AvatarEditorProvider;
